/**
 * Work Log 생성 폼 컴포넌트
 */

import { useState } from 'react'
import { WORK_LOG_CATEGORIES, WorkLogFormData } from '../../constants/workLog'
import { useCustomers } from '../../hooks/useCustomers'
import { useCreateWorkLog } from '../../hooks/useWorkLogs'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { RichTextEditor } from '../feedback/RichTextEditor'
import '../feedback/RichTextEditor.css'

interface WorkLogFormProps {
  onSuccess: () => void
  onCancel: () => void
}

export function WorkLogForm({ onSuccess, onCancel }: WorkLogFormProps) {
  const { customers } = useCustomers()
  const { createWorkLog, creating } = useCreateWorkLog()

  const [formData, setFormData] = useState<WorkLogFormData>({
    customerId: 0,
    category: '장애대응',
    title: '',
    description: '',
    expectedMinutes: null
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate customer selection
    if (formData.customerId === 0) {
      alert('고객사를 선택해주세요.')
      return
    }

    // Tiptap에서 내용을 비우면 <p></p> 같은 껍데기 HTML만 남는다. 공백 검증은 텍스트 길이 기준으로.
    const plainText = formData.description.replace(/<[^>]*>/g, '').trim()
    if (!plainText) {
      alert('작업 내용을 입력해주세요.')
      return
    }

    const success = await createWorkLog(formData)

    if (success) {
      onSuccess()
    } else {
      alert('Work Log 생성에 실패했습니다.')
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card>
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">새 업무 기록</h2>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* 고객사 선택 */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-100">고객사</label>
                <select
                  value={formData.customerId}
                  onChange={(e) => setFormData({ ...formData, customerId: Number(e.target.value) })}
                  className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 ${formData.customerId === 0 ? 'text-gray-400 dark:text-gray-500' : ''}`}
                  required
                  disabled={creating}
                >
                  <option value={0} disabled>고객사를 선택하세요</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.customerName}
                    </option>
                  ))}
                </select>
              </div>

              {/* 카테고리 선택 */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-100">카테고리</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                  required
                  disabled={creating}
                >
                  {WORK_LOG_CATEGORIES.filter((c) => c !== 'all').map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* 제목 입력 */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-100">제목</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="작업 제목을 입력하세요"
                  required
                  disabled={creating}
                />
              </div>

              {/* 작업 내용 입력 (Tiptap 기반 리치텍스트 에디터 — 굵게/기울임/목록/인용/코드 지원) */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-100">작업 내용</label>
                <RichTextEditor
                  content={formData.description}
                  onChange={(html) => setFormData({ ...formData, description: html })}
                  placeholder="작업 상세 내용을 입력하세요"
                  readOnly={creating}
                />
              </div>

              {/* 예상 소요 시간 입력 */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-100">예상 소요 시간 (분)</label>
                <input
                  type="number"
                  value={formData.expectedMinutes || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, expectedMinutes: e.target.value ? Number(e.target.value) : null })
                  }
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                  min={1}
                  disabled={creating}
                />
              </div>

              {/* 버튼 */}
              <div className="flex gap-2 pt-4">
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-md hover:shadow-lg
                             dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-50 dark:border dark:border-gray-500"
                  disabled={creating}
                >
                  {creating ? '생성 중...' : '시작하기'}
                </Button>
                <Button
                  type="button"
                  onClick={onCancel}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300
                             dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                  disabled={creating}
                >
                  취소
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Card>
    </div>
  )
}

export default WorkLogForm
